import { Workbox } from "workbox-window";

export interface PWAInstallPrompt {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

class PWAManager {
  private wb: Workbox | null = null;
  private installPrompt: PWAInstallPrompt | null = null;
  private isInstallable = false;
  private installListeners: Array<(canInstall: boolean) => void> = [];
  private updateListeners: Array<() => void> = [];

  constructor() {
    // Guard: never register SW inside iframes or Lovable preview hosts
    const isInIframe = (() => {
      try {
        return window.self !== window.top;
      } catch {
        return true;
      }
    })();
    const isPreviewHost =
      window.location.hostname.includes("id-preview--") ||
      window.location.hostname.includes("lovableproject.com");

    if (isInIframe || isPreviewHost) {
      // Unregister any stale SWs in preview contexts
      navigator.serviceWorker?.getRegistrations().then((regs) => {
        regs.forEach((r) => r.unregister());
      });
      return;
    }

    this.initializeServiceWorker();
    this.setupInstallPrompt();
  }

  private initializeServiceWorker() {
    if ("serviceWorker" in navigator) {
      this.wb = new Workbox("/sw.js");

      // Listen for service worker updates
      this.wb.addEventListener("waiting", () => {
        this.notifyUpdateListeners();
      });

      // Register the service worker
      this.wb
        .register()
        .then(() => {
          console.log("✅ Service Worker registered successfully");
        })
        .catch((error) => {
          console.error("❌ Service Worker registration failed:", error);
        });
    }
  }

  private setupInstallPrompt() {
    window.addEventListener("beforeinstallprompt", (e) => {
      e.preventDefault();
      this.installPrompt = e as any;
      this.isInstallable = true;
      this.notifyInstallListeners(true);
    });

    window.addEventListener("appinstalled", () => {
      console.log("✅ PWA was installed");
      this.isInstallable = false;
      this.installPrompt = null;
      this.notifyInstallListeners(false);
    });
  }

  public async installApp(): Promise<boolean> {
    if (!this.installPrompt) {
      return false;
    }

    try {
      await this.installPrompt.prompt();
      const choiceResult = await this.installPrompt.userChoice;

      if (choiceResult.outcome === "accepted") {
        console.log("✅ User accepted the install prompt");
        return true;
      } else {
        console.log("❌ User dismissed the install prompt");
        return false;
      }
    } catch (error) {
      console.error("❌ Error during installation:", error);
      return false;
    }
  }

  public canInstall(): boolean {
    return this.isInstallable;
  }

  public isStandalone(): boolean {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone === true
    );
  }

  public onInstallAvailable(callback: (canInstall: boolean) => void) {
    this.installListeners.push(callback);
    callback(this.isInstallable);
  }

  public onUpdateAvailable(callback: () => void) {
    this.updateListeners.push(callback);
  }

  public async skipWaiting(): Promise<void> {
    if (this.wb) {
      await this.wb.messageSkipWaiting();
      window.location.reload();
    }
  }

  private notifyInstallListeners(canInstall: boolean) {
    this.installListeners.forEach((listener) => listener(canInstall));
  }

  private notifyUpdateListeners() {
    this.updateListeners.forEach((listener) => listener());
  }

  public isOnline(): boolean {
    return navigator.onLine;
  }

  public onNetworkChange(callback: (isOnline: boolean) => void) {
    const handleOnline = () => callback(true);
    const handleOffline = () => callback(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }

  public async getStorageQuota(): Promise<{
    quota: number;
    usage: number;
  } | null> {
    if ("storage" in navigator && "estimate" in navigator.storage) {
      try {
        const estimate = await navigator.storage.estimate();
        return {
          quota: estimate.quota || 0,
          usage: estimate.usage || 0,
        };
      } catch (error) {
        console.error("Error getting storage estimate:", error);
        return null;
      }
    }
    return null;
  }
}

// Export singleton instance
export const pwaManager = new PWAManager();

export const checkPWASupport = (): boolean => {
  return "serviceWorker" in navigator && "PushManager" in window;
};

export const getPWADisplayMode = (): string => {
  if (window.matchMedia("(display-mode: standalone)").matches) {
    return "standalone";
  }
  if (window.matchMedia("(display-mode: fullscreen)").matches) {
    return "fullscreen";
  }
  if (window.matchMedia("(display-mode: minimal-ui)").matches) {
    return "minimal-ui";
  }
  return "browser";
};

export const isPWAInstalled = (): boolean => {
  return getPWADisplayMode() === "standalone";
};
